import React from 'react';
import { number, string } from 'prop-types';
import { has } from 'lodash';
import { useCabinetVisualizerStore } from '../../../../store';
import {
  selectDesignProducts,
  getSegments,
} from '../../../../store/cabinetVisualizer/selectors';
import {
  cabinetToBoundingBox,
  getConstrainedWallHeight,
  getTotalBounding,
  segmentsToBoundingBox
} from '../utils';

import { DimensionArrow } from './DimensionArrow';
import { inchesToPxs } from '../../../../utils/wall-size-utils';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';

export const DimensionArrowsContainer = ({
  designId,
  rows,
  columns,
  pxInInches,
  zoom,
  anchorSide,
}) => {

  const { cabinetVisualizer } = useCabinetVisualizerStore();

  const products = (designId && cabinetVisualizer) ? selectDesignProducts(cabinetVisualizer, designId) : [];

  const zoomPx = zoom * pxInInches;
  const arrowInchesToPxs = (value) => inchesToPxs(value, zoomPx);
  const isMobile = useIsMobileDevice();

  // All the info the the currently selected cabinet to add
  const segments = segmentsToBoundingBox(getSegments(columns, getConstrainedWallHeight(rows)));
  const cabinetBoundings = products?.map((cab) => cabinetToBoundingBox(cab));
  const hasBaseProducts = products?.some((cab) => cab.productType === 'Base');
  const hasBaseFillers = products?.some((cab) => cab.productType === 'BaseFiller');
  const hasBaseFillerOnly = hasBaseFillers && !hasBaseProducts;

  const totalBounds = getTotalBounding(cabinetBoundings);

  function getCabinetBoundingsBySide() {
    return {
      left: cabinetBoundings.filter((curCab) => curCab.left === totalBounds.left),
      right: cabinetBoundings.filter((curCab) => curCab.right === totalBounds.right),
      top: cabinetBoundings.filter((curCab) => curCab.top <= segments[1].top),
      bottom: cabinetBoundings.filter((curCab) => curCab.bottom === segments[2].bottom
      || curCab.bottom === segments[3].bottom),
    };
  }
  const cabinetsBySide = getCabinetBoundingsBySide();

  function calculateArrows() {
    const padding = isMobile ? 5 : 3;
    const paddingLeft = padding + 2;
    const paddingRight = padding + 2;
    const size = 2;

    const hasTopCabinets = cabinetsBySide.top?.length > 0;
    const hasBottomCabinets = cabinetsBySide.bottom?.length > 0;

    const topYPosition = totalBounds.top - padding;

    // For vertical arrows, position them at the top of the respective cabinet group
    const topCabinetTop = hasTopCabinets ? Math.min(...cabinetsBySide.top.map((cabinet) => cabinet.top)) : 0;
    const bottomCabinetTop = hasBottomCabinets ? Math.min(...cabinetsBySide.bottom.map((cabinet) => cabinet.top)) : 0;

    const leftArrowYPosition = hasTopCabinets
      ? topCabinetTop
      : bottomCabinetTop;

    let topPosX = 0;
    let bottomPosX = 0;
    let topLength = 0;
    let bottomLength = 0;

    // Move the top and bottom arrows to the correct x position
    if (anchorSide === 'right') {
      if (hasTopCabinets) {
        const leftMostTop = Math.min(...cabinetsBySide.top.map((cur) => cur.left));
        topLength = totalBounds.right - leftMostTop;
        topPosX = leftMostTop;
      }
      if (hasBottomCabinets) {
        const leftMostBottom = Math.min(...cabinetsBySide.bottom.map((cur) => cur.left));
        bottomLength = totalBounds.right - leftMostBottom;
        bottomPosX = leftMostBottom;
      }
    } else {
      topPosX = totalBounds.left;
      bottomPosX = totalBounds.left;

      if (hasTopCabinets) {
        topLength = Math.max(...cabinetsBySide.top.map((cur) => cur.right)) - totalBounds.left;
      }
      if (hasBottomCabinets) {
        bottomLength = Math.max(...cabinetsBySide.bottom.map((cur) => cur.right)) - totalBounds.left;
      }
    }

    const result = {
      left: {
        posX: totalBounds.left - paddingLeft,
        posY: leftArrowYPosition,
        type: 'vertical',
        size,
        length: hasBaseFillerOnly ? totalBounds.bottom - totalBounds.top + 4.5 : totalBounds.bottom - totalBounds.top,
        key: 'left'
      },
      right: {
        posX: totalBounds.right + paddingRight,
        posY: leftArrowYPosition,
        type: 'vertical',
        size,
        length: totalBounds.bottom - totalBounds.top,
        key: 'right'
      },
      top: {
        posX: topPosX,
        posY: topYPosition,
        type: 'horizontal',
        size,
        length: topLength,
        key: 'top'
      },
      bottom: {
        posX: bottomPosX,
        posY: totalBounds.bottom + padding,
        type: 'horizontal',
        size,
        length: bottomLength,
        key: 'bottom'
      },
    };

    return result;
  }

  let arrowsArray = [];

  if (cabinetBoundings.length > 0) {
    const arrows = calculateArrows();
    arrowsArray = [];
    if (arrows.top.length > 0) {
      arrowsArray.push(arrows.top);
    }

    if (arrows.bottom.length > 0) {
      arrowsArray.push(arrows.bottom);
    }
    arrowsArray.push(arrows[anchorSide]);

  }

  return (
    <div className="dimension-arrows-container">
      {
        arrowsArray.map(({
          posX, posY, size, length, type, key
        }) => (
          <DimensionArrow
            key={key}
            posX={posX}
            posY={posY}
            size={size}
            length={length}
            type={type}
            scaleFn={arrowInchesToPxs}
          />
        ))
      }
    </div>
  );
};

DimensionArrowsContainer.propTypes = {
  designId: string.isRequired,
  rows: number,
  columns: number,
  anchorSide: string,
  pxInInches: number.isRequired,
  zoom: number,
};

DimensionArrowsContainer.defaultProps = {
  rows: 0,
  columns: 0,
  anchorSide: 'left',
  zoom: 1,
};
